import { Integration, IntegrationServices } from '@/modules/common/types/integrations';
import { mapStatusToIntegrationState } from '@/modules/onboarding/integrations-setup/mappers/integration-state.mapper';

import { IntegrationStatusDescriptor } from '../../../common/types/integration-status';
import { integrationsData } from './data';

/**
 * Merges API integration status with local integration definitions
 * Preserves the order defined in integrationsData and appends integrations from API that aren't in the local list
 *
 * @param apiIntegrations - Integration status data from API
 * @returns Merged integration list with API status applied
 */
export const mergeIntegrationsWithStatus = (
  apiIntegrations: IntegrationStatusDescriptor[]
): Integration[] => {
  // Create a map of API integrations by service for quick lookup
  const apiIntegrationMap = new Map<IntegrationServices, IntegrationStatusDescriptor>();
  apiIntegrations.forEach(apiIntegration => {
    apiIntegrationMap.set(apiIntegration.integration, apiIntegration);
  });

  // Start with local integrations data (preserves order)
  const mergedIntegrations: Integration[] = integrationsData.map(localIntegration => {
    const apiIntegration = apiIntegrationMap.get(localIntegration.service);

    if (apiIntegration) {
      // Update state and lastSyncedAt from API
      return {
        ...localIntegration,
        state: mapStatusToIntegrationState(apiIntegration.status),
        lastSyncedAt: apiIntegration.lastSyncedAt,
      };
    }

    // Keep local integration as-is if not in API response
    return localIntegration;
  });

  // Add any integrations from API that aren't in local list
  const localServiceSet = new Set(integrationsData.map(i => i.service));
  apiIntegrations.forEach(apiIntegration => {
    if (!localServiceSet.has(apiIntegration.integration)) {
      // Find matching local integration definition or create a minimal one
      const localDef = integrationsData.find(i => i.service === apiIntegration.integration);

      mergedIntegrations.push({
        service: apiIntegration.integration,
        dataTypes: localDef?.dataTypes || [],
        state: mapStatusToIntegrationState(apiIntegration.status),
        lastSyncedAt: apiIntegration.lastSyncedAt,
      });
    }
  });

  return mergedIntegrations;
};
